import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Row, Col, Alert, Card, InputGroup } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Order.css';
import { useTranslation } from 'react-i18next';

const EditOrder = () => {
  const { orderId } = useParams();
  const { user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderItems, setOrderItems] = useState({});
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchProducts, setSearchProducts] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sharedconfigs, setSharedConfigs] = useState([]);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const wwidth = window.innerWidth;
  let ns_max = 0, rp_max = 0, sa_max = 0;

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
      loadConfigs();
    }
  }, [orderId]);

  useEffect(() => {
    if (!searchProducts.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.item_desc.toLowerCase().includes(searchProducts.toLowerCase()) || product.item_code.toLowerCase().includes(searchProducts.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchProducts, products]);

  const getProductImage = (product) => {
    if (product.image_url) {
      return product.image_url;
    }
    // 根據部門返回預設圖片
    return product.div === '10'
      ? '/images/default-milk.png'
      : '/images/default-ice-cream.png';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const loadOrderDetails = async () => {
    setSearchProducts('');
    setLoading(true);
    try {
      // 載入訂單詳情
      const orderResponse = await api.get(`/orders/${orderId}`);
      const orderData = await orderResponse.data.data;
      setOrder(await orderData);

      // 載入該客戶的所有可訂購產品
      // const productsResponse = await api.get(`/products/customer/${orderData.cust_code}?div=${orderData.div || 10}`);
      var customerCode = orderData.cust_code;
      var div = orderData.div;
      const productsResponse = await api.get('/products', {
        params: { customerCode, div }
      });
      const productsData = await productsResponse.data.data || [];
      setProducts(await productsData);
      setFilteredProducts(await productsData);

      // 初始化現有訂單項目
      const initialOrderItems = {};
      productsData.forEach(product => {
        initialOrderItems[product.item_code] = {
          orderQty: 0,
          replaceQty: 0,
          sampleQty: 0
        };
      });

      // 填充現有訂單數據
      orderData.items.forEach(item => {
        if (item.tran_code === 'ZNS') {
          initialOrderItems[item.item_code].orderQty = item.qty;
        } else if (item.tran_code === 'ZRP') {
          initialOrderItems[item.item_code].replaceQty = item.qty;
        } else if (item.tran_code === 'ZSA') {
          initialOrderItems[item.item_code].sampleQty = item.qty;
        }
      });

      setOrderItems(initialOrderItems);
      setError('');
    } catch (err) {
      console.error('載入訂單詳情失敗:', err);
      setError(t('msg_loadOrderFailed'));
      window.scrollTo(0, 0);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = () => {
    setIsEnlarged(!isEnlarged);
  };

  const handleSearchChange = (e) => {
    setSearchProducts(e.target.value);
  };

  const loadConfigs = async () => {
    try {
      const configResponse = await api.get(`/sharedconfigs/`);
      setSharedConfigs(await configResponse.data.data);
    } catch (err) {
      setError(t('msg_loadconfigFail') + ': ' + (err.response?.data?.message || t('msg_tryLater')));
      window.scrollTo(0, 0);
      setSharedConfigs([]);
    }
  };

  const handleQtyChange = (itemCode, field, value, tranCode) => {
    // 確保數量不小於0
    const numValue = parseInt(value, 10);
    const newQty = isNaN(numValue) ? 0 : Math.max(0, numValue);
    const inputID = itemCode + "_" + tranCode;
    if (newQty < 0) {
      document.getElementById(inputID).value = '';
    } else {
      sharedconfigs.forEach(configs => {
        if (configs.zkey === 'ZNS_MAX') {
          ns_max = configs.zvalue;
        }
        if (configs.zkey === 'ZRP_MAX') {
          rp_max = configs.zvalue;
        }
        if (configs.zkey === 'ZSA_MAX') {
          sa_max = configs.zvalue;
        }
      });
      if (field === 'orderQty' && parseInt(newQty) > ns_max || field === 'replaceQty' && parseInt(newQty) > rp_max || field === 'sampleQty' && parseInt(newQty) > sa_max) {
        return;
      } else {
        setOrderItems(prev => ({
          ...prev,
          [itemCode]: {
            ...prev[itemCode],
            [field]: newQty
          }
        }));
      }
    }
  }

  const handleIncrement = (itemCode, field, tranCode) => {
    const inputID = itemCode + "_" + tranCode;
    sharedconfigs.forEach(configs => {
      if (configs.zkey === 'ZNS_MAX') {
        ns_max = configs.zvalue;
      }
      if (configs.zkey === 'ZRP_MAX') {
        rp_max = configs.zvalue;
      }
      if (configs.zkey === 'ZSA_MAX') {
        sa_max = configs.zvalue;
      }
    });

    if (field === 'orderQty' && parseInt(document.getElementById(inputID).value) + 1 > ns_max ||
      field === 'replaceQty' && parseInt(document.getElementById(inputID).value) + 1 > rp_max ||
      field === 'sampleQty' && parseInt(document.getElementById(inputID).value) + 1 > sa_max) {
      return;
    }

    setOrderItems(prev => ({
      ...prev,
      [itemCode]: {
        ...prev[itemCode],
        [field]: parseInt((prev[itemCode][field] || 0)) + 1
      }
    }));
  };

  const handleDecrement = (itemCode, field) => {
    setOrderItems(prev => ({
      ...prev,
      [itemCode]: {
        ...prev[itemCode],
        [field]: Math.max(0, (prev[itemCode][field] || 0) - 1)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 檢查是否有選擇產品
    const hasItems = Object.values(orderItems).some(
      item => item.orderQty > 0 || item.replaceQty > 0 || item.sampleQty > 0
    );

    if (!hasItems) {
      setError(t('msg_noItem'));
      window.scrollTo(0, 0);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // 準備訂單項目
      const items = [];
      Object.keys(orderItems).forEach(itemCode => {
        const item = orderItems[itemCode];

        if (item.orderQty > 0) {
          items.push({
            tran_code: 'ZNS', // 普通銷售
            item_code: itemCode,
            qty: item.orderQty
          });
        }

        if (item.replaceQty > 0) {
          items.push({
            tran_code: 'ZRP', // 退貨
            item_code: itemCode,
            qty: item.replaceQty
          });
        }

        if (item.sampleQty > 0) {
          items.push({
            tran_code: 'ZSA', // 退貨
            item_code: itemCode,
            qty: item.sampleQty
          });
        }
      });

      items.sort((a, b) => a.item_code.localeCompare(b.item_code));

      // 更新訂單
      await api.put(`/orders/${orderId}`, {
        items
      });

      setSuccess(t('msg_updateSuccess'));
      window.scrollTo(0, 0);

      // 重新載入訂單數據
      setTimeout(() => {
        navigate(`/orders/${orderId}`);
      }, 2000);
    } catch (err) {
      console.error('更新訂單失敗:', err);
      setError(t('msg_updateFailed') + ': ' + (err.response?.data?.message || t('msg_tryLater')));
      window.scrollTo(0, 0);
    } finally {
      setTimeout(() => {
        setSaving(false);
      }, 3000);
    }
  };

  const handleCancel = () => {
    navigate(`/orders/${orderId}`);
  };

  return (
    <Container className="edit-order-container p-1">
      <Row className="mb-1 align-items-center">
        <Col>
          <h2>編輯訂單</h2>
          <h5 className="text-muted">(訂單號碼: {orderId})</h5>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : order ? (
        <Form onSubmit={handleSubmit}>
          <div className="order-info">
            <p className="mb-1"><strong>店鋪:</strong> {order.cust_name} ({order.cust_code})</p>
            <p className="mb-1"><strong>送貨日期:</strong> {formatDate(order.delivery_date)}</p>
          </div>
          <Row className="mb-4">
            <Col md={12}>
              <Card className="h-100">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{t('productList')}</span>
                    <InputGroup className="product-search" style={{ width: '60%' }}>
                      <Form.Control
                        placeholder={t('searchProduct')}
                        value={searchProducts}
                        onChange={handleSearchChange}
                        disabled={loading}
                      />
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">

                  <div className="product-grid">
                    {filteredProducts.map((product) => (
                      <div key={product.item_code} className="product-card">
                        <span className="product-title">
                          {product.item_desc}
                        </span>
                        <div className={user.role === 0 ? 'product-image2' : 'product-image'}>
                          <img
                            src={getProductImage(product)}
                            alt={product.item_desc}
                            className={`image ${isEnlarged ? 'enlarged' : ''}`}
                            onClick={handleImageClick}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/products/default.svg';
                            }}
                          />
                          <div className="product-info">
                            <table>
                              <tbody>
                                <tr>
                                  <td className="type-title">{t('sales')}</td>
                                  <td>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      style={{ width: '29px', height: '29px', borderStyle: 'solid', borderColor: 'silver' }}
                                      className="w-10" /* 讓按鈕寬度100% */
                                      onClick={() => handleDecrement(product.item_code, 'orderQty')}
                                      disabled={loading}
                                    >
                                      -
                                    </Button>
                                  </td>
                                  <td>
                                    <Form.Control
                                      id={product.item_code + "_ZNS"}
                                      type="number"
                                      min="1"
                                      max="999"
                                      style={{ minWidth: '40px' }}
                                      value={parseInt(orderItems[product.item_code]?.orderQty) || ''}
                                      onChange={(e) => handleQtyChange(product.item_code, 'orderQty', e.target.value, 'ZNS')}
                                      size="sm"
                                      disabled={loading}
                                    />
                                  </td>
                                  <td>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      style={{ width: '29px', height: '29px', borderStyle: 'solid', borderColor: 'silver' }}
                                      className="w-10" /* 讓按鈕寬度100% */
                                      onClick={() => handleIncrement(product.item_code, 'orderQty', 'ZNS')}
                                      disabled={loading}
                                    >
                                      +
                                    </Button>
                                  </td>
                                </tr>
                                {product.no_rp === 'X' ? <tr></tr> :
                                  <tr>
                                    <td className="type-title">{t('replace')}</td>
                                    <td>
                                      <Button
                                        variant="light"
                                        size="sm"
                                        style={{ width: '29px', height: '29px', borderStyle: 'solid', borderColor: 'silver' }}
                                        className="w-10" /* 讓按鈕寬度100% */
                                        onClick={() => handleDecrement(product.item_code, 'replaceQty')}
                                        disabled={loading}
                                      >
                                        -
                                      </Button>
                                    </td>
                                    <td>
                                      <Form.Control
                                        id={product.item_code + "_ZRP"}
                                        type="number"
                                        min="1"
                                        max="999"
                                        style={{ minWidth: '40px' }}
                                        value={parseInt(orderItems[product.item_code]?.replaceQty) || ''}
                                        onChange={(e) => handleQtyChange(product.item_code, 'replaceQty', e.target.value, 'ZRP')}
                                        size="sm"
                                        disabled={loading}
                                      />
                                    </td>
                                    <td>
                                      <Button
                                        variant="light"
                                        size="sm"
                                        style={{ width: '29px', height: '29px', borderStyle: 'solid', borderColor: 'silver' }}
                                        className="w-10" /* 讓按鈕寬度100% */
                                        onClick={() => handleIncrement(product.item_code, 'replaceQty', 'ZRP')}
                                        disabled={loading}
                                      >
                                        +
                                      </Button>
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col className="d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                取消
              </Button>

              <Button
                id="submitchange"
                variant="danger"
                type="submit"
                disabled={saving}
                style={wwidth <= 768 ? { position: 'fixed', zIndex: '1000', right: '2%', top: '88%' } : {}}
              >
                {saving ? '更新中...' : '更新訂單'}
              </Button>
            </Col>
          </Row>
        </Form>
      ) : (
        <div className="text-center my-5">
          <p>找不到訂單資訊</p>
          <Button variant="outline-primary" onClick={() => navigate('/orders')}>
            返回訂單列表
          </Button>
        </div>
      )}
    </Container>
  );
};

export default EditOrder;